import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  RefreshControl,
  Image,
  StyleSheet,
  ScrollView,
} from 'react-native';
import {
  Searchbar,
  Chip,
  Card,
  Button,
  Switch,
  Title,
  Paragraph,
} from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import apiService, { Distillery } from '../services/ApiService';
import FilterChip from '../components/FilterChip';
import { useTheme } from '../contexts/ThemeContext';
import { ThemedContainer, ThemedText } from '../components/ThemedContainer';

interface DistilleriesScreenProps {
  navigation: any;
}

interface FilterGroup {
  type: 'country' | 'region' | 'parent_company' | 'facility_type';
  selected: string[];
  logic: 'AND' | 'OR';
}

const DistilleriesScreen: React.FC<DistilleriesScreenProps> = ({ navigation }) => {
  const { themeData } = useTheme();
  const [distilleries, setDistilleries] = useState<Distillery[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [countries, setCountries] = useState<string[]>([]);
  const [regions, setRegions] = useState<string[]>([]);
  const [parentCompanies, setParentCompanies] = useState<string[]>([]);
  const [facilityTypes, setFacilityTypes] = useState<string[]>([]);
  const [expandedFilters, setExpandedFilters] = useState<{
    country: number;
    region: number;
    parent_company: number;
    facility_type: number;
  }>({
    country: 12,
    region: 6,
    parent_company: 8,
    facility_type: 6,
  });
  const [filters, setFilters] = useState<FilterGroup[]>([
    { type: 'country', selected: [], logic: 'OR' },
    { type: 'region', selected: [], logic: 'OR' },
    { type: 'parent_company', selected: [], logic: 'OR' },
    { type: 'facility_type', selected: [], logic: 'OR' }
  ]);
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load distilleries data
      const distilleriesData = await apiService.getDistilleries();
      setDistilleries(distilleriesData);
      
      // Extract unique values for filters from distilleries data
      const uniqueCountries = Array.from(new Set(distilleriesData.map(d => d.country).filter((country): country is string => Boolean(country))));
      const uniqueRegions = Array.from(new Set(distilleriesData.map(d => d.region).filter((region): region is string => Boolean(region))));
      const uniqueParentCompanies = Array.from(new Set(distilleriesData.map(d => d.parent_company).filter((company): company is string => Boolean(company))));
      const uniqueFacilityTypes = Array.from(new Set(distilleriesData.map(d => d.facility_type).filter((type): type is string => Boolean(type))));
      
      setCountries(uniqueCountries.sort());
      setRegions(uniqueRegions.sort());
      setParentCompanies(uniqueParentCompanies.sort());
      setFacilityTypes(uniqueFacilityTypes.sort());
      
    } catch (error) {
      console.error('Error loading distilleries data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const onRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  // Memoized filtering to prevent unnecessary re-computations
  const filteredDistilleries = useMemo(() => {
    let filtered = [...distilleries];
    
    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(distillery =>
        distillery.name.toLowerCase().includes(query) ||
        (distillery.country && distillery.country.toLowerCase().includes(query)) ||
        (distillery.region && distillery.region.toLowerCase().includes(query)) ||
        (distillery.city && distillery.city.toLowerCase().includes(query))
      );
    }
    
    // Apply filters
    filters.forEach(filterGroup => {
      if (filterGroup.selected.length > 0) {
        filtered = filtered.filter(distillery => {
          let values: string[] = [];
          
          switch (filterGroup.type) {
            case 'country':
              values = distillery.country ? [distillery.country] : [];
              break;
            case 'region':
              values = distillery.region ? [distillery.region] : [];
              break;
            case 'parent_company':
              values = distillery.parent_company ? [distillery.parent_company] : [];
              break;
            case 'facility_type':
              values = distillery.facility_type ? [distillery.facility_type] : [];
              break;
          }
          
          if (filterGroup.logic === 'AND') {
            return filterGroup.selected.every(selectedValue =>
              values.some(value => value === selectedValue)
            );
          } else {
            return filterGroup.selected.some(selectedValue =>
              values.some(value => value === selectedValue)
            );
          }
        });
      }
    });
    
    return filtered;
  }, [distilleries, searchQuery, filters]);

  const toggleFilter = (filterType: FilterGroup['type'], value: string) => {
    setFilters(prev => prev.map(filter => {
      if (filter.type === filterType) {
        const isSelected = filter.selected.includes(value);
        return {
          ...filter,
          selected: isSelected
            ? filter.selected.filter(item => item !== value)
            : [...filter.selected, value]
        };
      }
      return filter;
    }));
  };

  const clearAllFilters = () => {
    setFilters(prev => prev.map(filter => ({ ...filter, selected: [] })));
    setSearchQuery('');
  };

  const getAllSelectedFilters = () => {
    return filters.reduce((all, filter) => [...all, ...filter.selected], [] as string[]);
  };

  const renderDistillery = ({ item }: { item: Distillery }) => (
    <TouchableOpacity onPress={() => navigation.navigate('DistilleryDetail', { distilleryId: item.id })}>
      <Card style={[styles.distilleryCard, { backgroundColor: themeData.colors.cardBackground }]}>
        <Card.Content>
          <View style={styles.distilleryHeader}>
            <View style={styles.distilleryInfo}>
              <Title style={[styles.distilleryTitle, { color: themeData.colors.text }]}>
                {item.name || 'Unknown Distillery'}
              </Title>
              <View style={styles.distilleryMeta}>
                {item.country && item.country.trim() && (
                  <FilterChip
                    label={item.country}
                    selected={filters.find(f => f.type === 'country')?.selected.includes(item.country) || false}
                    onPress={() => toggleFilter('country', item.country || '')}
                    size="small"
                  />
                )}
                {item.region && item.region.trim() && (
                  <FilterChip
                    label={item.region}
                    selected={filters.find(f => f.type === 'region')?.selected.includes(item.region) || false}
                    onPress={() => toggleFilter('region', item.region!)}
                    size="small"
                  />
                )}
                {item.city && item.city.trim() && (
                  <FilterChip
                    label={item.city}
                    selected={false}
                    onPress={() => {}}
                    size="small"
                  />
                )}
              </View>
            </View>
          </View>
          
          {/* Enhanced distillery details */}
          <View style={styles.distilleryDetails}>
            {/* Location & Identity */}
            <View style={styles.detailRow}>
              {item.founded_year && item.founded_year.toString().trim() && (
                <View style={styles.detailItem}>
                  <Text style={[styles.detailLabel, { color: themeData.colors.highlight1 }]}>Founded:</Text>
                  <Text style={[styles.detailValue, { color: themeData.colors.text }]}>{item.founded_year.toString()}</Text>
                </View>
              )}
              {item.facility_type && item.facility_type.trim() && (
                <View style={styles.detailItem}>
                  <Text style={[styles.detailLabel, { color: themeData.colors.highlight2 }]}>Type:</Text>
                  <Text style={[styles.detailValue, { color: themeData.colors.text }]}>{item.facility_type}</Text>
                </View>
              )}
            </View>
            
            {/* Parent Company */}
            {item.parent_company && item.parent_company.trim() && (
              <View style={styles.detailRow}>
                <View style={styles.detailItem}>
                  <Text style={[styles.detailLabel, { color: themeData.colors.highlight3 }]}>Parent:</Text>
                  <Text style={[styles.detailValue, { color: themeData.colors.text }]}>{item.parent_company}</Text>
                </View>
              </View>
            )}
          </View>
        </Card.Content>
      </Card>
    </TouchableOpacity>
  );

  if (loading) {
    return (
      <View style={[styles.container, styles.centered, { backgroundColor: themeData.colors.background }]}>
        <Text style={{ color: themeData.colors.text }}>Loading distilleries...</Text>
      </View>
    );
  }

  return (
    <ThemedContainer style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Title style={styles.headerTitle}>
          🏭 Distillery Database
        </Title>
        <Paragraph style={styles.headerSubtitle}>
          Discover {distilleries.length} distilleries from around the world
        </Paragraph>
      </View>

      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <Searchbar
          placeholder="Search distilleries..."
          onChangeText={setSearchQuery}
          value={searchQuery}
          style={[styles.searchBar, { backgroundColor: themeData.colors.cardBackground }]}
          inputStyle={{ color: themeData.colors.text }}
        />
      </View>

      {/* Filter Section */}
      <View style={styles.filtersContainer}>
        <View style={styles.filterHeader}>
          <Text style={styles.filterTitle}>Filters</Text>
          <TouchableOpacity
            style={styles.collapseButton}
            onPress={() => setFiltersCollapsed(!filtersCollapsed)}
          >
            <Text style={{ color: themeData.colors.primary }}>
              {filtersCollapsed ? 'Expand' : 'Collapse'}
            </Text>
          </TouchableOpacity>
        </View>

        {getAllSelectedFilters().length > 0 && !filtersCollapsed && (
          <View style={styles.selectedFiltersContainer}>
            <View style={styles.selectedFiltersLabel}>
              <Text style={styles.selectedFiltersLabelText}>
                Selected filters ({getAllSelectedFilters().length}):
              </Text>
            </View>
            <ScrollView
              horizontal
              style={styles.selectedFiltersScroll}
              showsHorizontalScrollIndicator={false}
            >
              <View style={styles.selectedFiltersChips}>
                {getAllSelectedFilters().map((filter) => (
                  <FilterChip
                    key={filter}
                    label={filter}
                    selected={true}
                    onPress={() => {
                      // Find which filter type this belongs to and remove it
                      filters.forEach(filterGroup => {
                        if (filterGroup.selected.includes(filter)) {
                          toggleFilter(filterGroup.type, filter);
                        }
                      });
                    }}
                    size="small"
                  />
                ))}
              </View>
            </ScrollView>
          </View>
        )}

        {!filtersCollapsed && (
          <ScrollView 
            style={styles.filterScrollView}
            showsVerticalScrollIndicator={false}
          >
            <View style={styles.filterContent}>
              {/* Countries */}
              <View style={styles.filterSection}>
                <Text style={styles.filterSectionTitle}>Countries</Text>
                <View style={styles.chipContainer}>
                  {countries.slice(0, expandedFilters.country).map((country) => {
                    const isSelected = filters.find(f => f.type === 'country')?.selected.includes(country) || false;
                    return (
                      <FilterChip
                        key={country}
                        label={country}
                        selected={isSelected}
                        onPress={() => toggleFilter('country', country)}
                        size="large"
                      />
                    );
                  })}
                </View>
                {countries.length > expandedFilters.country && (
                  <TouchableOpacity
                    style={styles.showMoreButton}
                    onPress={() => setExpandedFilters(prev => ({ 
                      ...prev, 
                      country: Math.min(prev.country + 12, countries.length) 
                    }))}
                  >
                    <Text style={{ color: themeData.colors.primary }}>
                      {expandedFilters.country >= countries.length ? 'Show Less' : `Show ${Math.min(12, countries.length - expandedFilters.country)} More`}
                    </Text>
                  </TouchableOpacity>
                )}
              </View>

              {/* Regions */}
              <View style={styles.filterSection}>
                <Text style={styles.filterSectionTitle}>Regions</Text>
                <View style={styles.chipContainer}>
                  {regions.slice(0, expandedFilters.region).map((region) => {
                    const isSelected = filters.find(f => f.type === 'region')?.selected.includes(region) || false;
                    return (
                      <FilterChip
                        key={region}
                        label={region}
                        selected={isSelected}
                        onPress={() => toggleFilter('region', region)}
                        size="large"
                      />
                    );
                  })}
                </View>
                {regions.length > expandedFilters.region && (
                  <TouchableOpacity
                    style={styles.showMoreButton}
                    onPress={() => setExpandedFilters(prev => ({ 
                      ...prev, 
                      region: Math.min(prev.region + 6, regions.length) 
                    }))}
                  >
                    <Text style={{ color: themeData.colors.primary }}>
                      {expandedFilters.region >= regions.length ? 'Show Less' : `Show ${Math.min(6, regions.length - expandedFilters.region)} More`}
                    </Text>
                  </TouchableOpacity>
                )}
              </View>



              {/* Parent Companies */}
              <View style={styles.filterSection}>
                <Text style={styles.filterSectionTitle}>Parent Companies</Text>
                <View style={styles.chipContainer}>
                  {parentCompanies.slice(0, expandedFilters.parent_company).map((company) => {
                    const isSelected = filters.find(f => f.type === 'parent_company')?.selected.includes(company) || false;
                    return (
                      <FilterChip
                        key={company}
                        label={company}
                        selected={isSelected}
                        onPress={() => toggleFilter('parent_company', company)}
                        size="large"
                      />
                    );
                  })}
                </View>
                {parentCompanies.length > expandedFilters.parent_company && (
                  <TouchableOpacity
                    style={styles.showMoreButton}
                    onPress={() => setExpandedFilters(prev => ({ 
                      ...prev, 
                      parent_company: Math.min(prev.parent_company + 8, parentCompanies.length) 
                    }))}
                  >
                    <Text style={{ color: themeData.colors.primary }}>
                      {expandedFilters.parent_company >= parentCompanies.length ? 'Show Less' : `Show ${Math.min(8, parentCompanies.length - expandedFilters.parent_company)} More`}
                    </Text>
                  </TouchableOpacity>
                )}
              </View>

              {/* Facility Types */}
              <View style={styles.filterSection}>
                <Text style={styles.filterSectionTitle}>Facility Types</Text>
                <View style={styles.chipContainer}>
                  {facilityTypes.slice(0, expandedFilters.facility_type).map((type) => {
                    const isSelected = filters.find(f => f.type === 'facility_type')?.selected.includes(type) || false;
                    return (
                      <FilterChip
                        key={type}
                        label={type}
                        selected={isSelected}
                        onPress={() => toggleFilter('facility_type', type)}
                        size="large"
                      />
                    );
                  })}
                </View>
                {facilityTypes.length > expandedFilters.facility_type && (
                  <TouchableOpacity
                    style={styles.showMoreButton}
                    onPress={() => setExpandedFilters(prev => ({ 
                      ...prev, 
                      facility_type: Math.min(prev.facility_type + 6, facilityTypes.length) 
                    }))}
                  >
                    <Text style={{ color: themeData.colors.primary }}>
                      {expandedFilters.facility_type >= facilityTypes.length ? 'Show Less' : `Show ${Math.min(6, facilityTypes.length - expandedFilters.facility_type)} More`}
                    </Text>
                  </TouchableOpacity>
                )}
              </View>


            </View>
          </ScrollView>
        )}
      </View>

      <FlatList
        data={filteredDistilleries}
        renderItem={renderDistillery}
        keyExtractor={(item) => item.id.toString()}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Title style={[styles.emptyTitle, { color: themeData.colors.text }]}>
              No distilleries found
            </Title>
            <Paragraph style={[styles.emptyDescription, { color: themeData.colors.textSecondary }]}>
              Try adjusting your search or filters to find more distilleries.
            </Paragraph>
            {getAllSelectedFilters().length > 0 && (
              <Button
                mode="outlined"
                onPress={clearAllFilters}
                style={styles.clearFiltersButton}
              >
                Clear all filters
              </Button>
            )}
          </View>
        }
        contentContainerStyle={styles.distilleryList}
      />
    </ThemedContainer>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    padding: 16,
    alignItems: 'center',
    marginBottom: 8,
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 8,
  },
  headerSubtitle: {
    fontSize: 16,
    textAlign: 'center',
    opacity: 0.8,
  },
  searchContainer: {
    padding: 16,
    paddingBottom: 8,
  },
  searchBar: {
    margin: 8,
    elevation: 2,
  },
  filtersContainer: {
    marginHorizontal: 16,
    marginBottom: 0,
    paddingBottom: 0,
    padding: 0,
  },
  filterHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  filterTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  collapseButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  selectedFiltersContainer: {
    marginBottom: 8,
  },
  selectedFiltersLabel: {
    marginBottom: 8,
  },
  selectedFiltersLabelText: {
    fontSize: 14,
    fontWeight: 'bold',
    marginRight: 8,
  },
  selectedFiltersScroll: {
    maxHeight: 50,
  },
  selectedFiltersChips: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
  },
  filterScrollView: {
    maxHeight: 300,
    borderBottomWidth: 2,
    elevation: 3,
    padding: 0,
  },
  filterContent: {
    paddingBottom: 2,
    padding: 0,
  },
  filterSection: {
    marginHorizontal: 16,
    marginBottom: 2,
    padding: 0,
  },
  filterSectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  chipContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  showMoreButton: {
    marginTop: 4,
  },
  distilleryDetails: {
    marginBottom: 12,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  detailItem: {
    flex: 1,
  },
  detailLabel: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  detailValue: {
    fontSize: 14,
  },
  descriptionContainer: {
    marginTop: 8,
  },
  descriptionLabel: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  descriptionText: {
    fontSize: 13,
    lineHeight: 18,
  },
  distilleryList: {
    padding: 0,
    paddingTop: 0,
    paddingBottom: 0,
    paddingHorizontal: 0,
    margin: 0,
  },
  distilleryCard: {
    marginBottom: 16,
  },
  distilleryHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  distilleryInfo: {
    flex: 1,
  },
  distilleryTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  distilleryMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  distilleryFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  viewDetailsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
  },
  viewDetailsText: {
    fontSize: 14,
    fontWeight: 'bold',
    marginRight: 4,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  emptyTitle: {
    fontSize: 20,
    marginTop: 16,
    marginBottom: 8,
  },
  emptyDescription: {
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 16,
  },
  clearFiltersButton: {
    marginTop: 8,
  },
});

export default DistilleriesScreen;
